import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityService } from '@vanilla/services/ActivityService.js';
import { DealsApiService } from '@vanilla/services/crm/DealsApiService.js';
import { auth } from '@vanilla/services/AuthService.js';
import { formatDateDisplay } from '@vanilla/utils/Utils.js';
import { ToastManager } from '@/lib/toast.js';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { NativeCheckbox } from '@/components/ui/checkbox';
import {
    Drawer,
    DrawerContent,
    DrawerTitle,
} from '@/components/ui/drawer';
import { confirmAlert } from '@/lib/confirmAlert';

const DRAWER_PER_PAGE = 30;

function isCommentItem(item) {
    const type = String(item?.type || item?.activityType || item?.kind || '').toLowerCase();
    return type === 'comment' || type === 'note';
}

function formatTs(value) {
    if (!value) return '—';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return String(value);
    }
}

function mentionTokenForUser(user) {
    const email = String(user?.email || '').trim();
    if (email.includes('@')) {
        const local = email.split('@')[0].toLowerCase();
        return local.replace(/[^a-z0-9._-]/g, '');
    }
    const base = String(user?.name || user?.id || 'user').toLowerCase();
    return base.replace(/\s+/g, '.').replace(/[^a-z0-9._-]/g, '');
}

function extractMentionIdsFromText(text, users) {
    const tokens = [...String(text || '').matchAll(/@([a-zA-Z0-9._-]+)/g)].map((m) => m[1].toLowerCase());
    if (!tokens.length) return [];
    const tokenToId = new Map();
    (users || []).forEach((u) => {
        const token = mentionTokenForUser(u);
        if (token) tokenToId.set(token.toLowerCase(), u.id);
    });
    const ids = [];
    const seen = new Set();
    tokens.forEach((token) => {
        const id = tokenToId.get(token);
        if (id && !seen.has(id)) {
            seen.add(id);
            ids.push(id);
        }
    });
    return ids;
}

function MentionText({ text }) {
    const parts = String(text || '').split(/(@[a-zA-Z0-9._-]+)/g);
    return parts.map((part, i) => (
        /^@[a-zA-Z0-9._-]+$/.test(part)
            ? <span key={i} className="crm-mention">{part}</span>
            : <React.Fragment key={i}>{part}</React.Fragment>
    ));
}

function mapActivity(a, kind = 'activity') {
    return {
        id: a.id,
        kind: a.source === 'system' || kind === 'system' ? 'system' : kind,
        source: a.source || kind,
        type: a.type || a.activityType || (kind === 'system' ? 'system' : ''),
        subject: a.subject || '',
        body: a.body || a.description || '',
        description: a.description || a.body || '',
        createdAt: a.createdAt || a.created_at,
        createdBy: a.createdBy || a.created_by,
        userId: a.userId || a.createdBy || a.created_by,
        isInternal: a.isInternal || a.is_internal,
        deletedAt: a.deletedAt || a.deleted_at,
        systemEventType: a.systemEventType || a.system_event_type,
        dueDate: a.dueDate || a.due_date || '',
    };
}

function activityDrawerDateLabel(timestamp) {
    const dt = timestamp ? new Date(timestamp) : null;
    if (!dt || Number.isNaN(dt.getTime())) return 'Unknown date';
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const startOfDate = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
    const diffDays = Math.floor((startOfToday - startOfDate) / (24 * 60 * 60 * 1000));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return formatDateDisplay(timestamp);
}

function MentionMenu({ users, value, cursor, visible, onPick }) {
    const suggestions = useMemo(() => {
        if (!visible) return [];
        const before = String(value || '').slice(0, cursor ?? 0);
        const match = before.match(/@([a-zA-Z0-9._-]*)$/);
        if (!match) return [];
        const query = match[1].toLowerCase();
        return (users || [])
            .map((u) => ({
                id: u.id,
                name: u.name || u.email || u.id,
                email: u.email || '',
                token: mentionTokenForUser(u),
            }))
            .filter((u) => {
                const nameMatch = u.name.toLowerCase().includes(query);
                const emailMatch = u.email.toLowerCase().includes(query);
                const tokenMatch = u.token.toLowerCase().includes(query);
                return nameMatch || emailMatch || tokenMatch;
            })
            .slice(0, 8);
    }, [users, value, cursor, visible]);

    if (!visible || !suggestions.length) return null;

    return (
        <div className="sidebar-mention-menu" role="listbox">
            {suggestions.map((u) => (
                <button
                    key={u.id}
                    type="button"
                    className="sidebar-mention-option"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onPick(u);
                    }}
                >
                    @{u.token}{' '}
                    <span className="sidebar-mention-secondary">{u.name}</span>
                </button>
            ))}
        </div>
    );
}

/**
 * Comments & Timeline sidebar + activity drawer (SoT DealFormManager).
 */
export default function DealSidebarTimeline({ dealId, dealName = '', users = [], initialFilter = null, autoOpenDrawer = false, highlightId = null }) {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState('all');
    const [comment, setComment] = useState('');
    const [isInternal, setIsInternal] = useState(false);
    const [sending, setSending] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerFilter, setDrawerFilter] = useState('all');
    const [drawerDateRange, setDrawerDateRange] = useState('all');
    const [drawerSortOrder, setDrawerSortOrder] = useState('');
    const [drawerSearch, setDrawerSearch] = useState('');
    const [drawerItems, setDrawerItems] = useState([]);
    const [drawerPage, setDrawerPage] = useState(1);
    const [drawerHasMore, setDrawerHasMore] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [mentionCtx, setMentionCtx] = useState({ source: null, cursor: 0 });

    const sidebarInputRef = useRef(null);
    const drawerInputRef = useRef(null);

    const userMap = useMemo(
        () => Object.fromEntries(users.map((u) => [String(u.id), u.name || u.email || u.id])),
        [users],
    );

    const loadTimeline = useCallback(async () => {
        if (!dealId) {
            setItems([]);
            return;
        }
        try {
            const [userRows, systemRows, nextStepsResponse] = await Promise.all([
                ActivityService.getForEntity('deal', dealId, { perPage: 10, sortOrder: 'desc', source: 'user' }),
                ActivityService.getForEntity('deal', dealId, { perPage: 10, sortOrder: 'desc', source: 'system' }),
                DealsApiService.getNextSteps(dealId).catch(() => ({ data: [] })),
            ]);
            const activities = (userRows || []).map((a) => mapActivity(a, 'activity'));
            const systemEvents = (systemRows || []).map((a) => mapActivity(a, 'system'));
            const nsRows = Array.isArray(nextStepsResponse) ? nextStepsResponse : (nextStepsResponse?.data || []);
            const nextSteps = nsRows.map((s) => ({
                id: s.id || `next-step-${s.action}`,
                kind: 'activity',
                source: 'user',
                type: 'next_step',
                subject: s.action || 'Next step',
                body: '',
                createdAt: s.updated_at || s.created_at || s.due_date || '',
                createdBy: null,
            }));
            const merged = [...activities, ...nextSteps, ...systemEvents].sort((a, b) =>
                String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
            );
            setItems(merged);
        } catch (err) {
            console.warn('[DealSidebarTimeline] load failed', err);
            setItems([]);
        }
    }, [dealId]);

    const loadDrawerPage = useCallback(async (page, { append = false } = {}) => {
        if (!dealId) return;
        setDrawerLoading(true);
        try {
            const [userRows, systemRows, nextStepsResponse] = await Promise.all([
                ActivityService.getForEntity('deal', dealId, {
                    page,
                    perPage: DRAWER_PER_PAGE,
                    sortOrder: 'desc',
                    source: 'user',
                }),
                ActivityService.getForEntity('deal', dealId, {
                    page,
                    perPage: DRAWER_PER_PAGE,
                    sortOrder: 'desc',
                    source: 'system',
                }),
                page === 1
                    ? DealsApiService.getNextSteps(dealId).catch(() => ({ data: [] }))
                    : Promise.resolve({ data: [] }),
            ]);
            const userActivities = (userRows || []).map((a) => mapActivity(a, 'activity'));
            const systemActivities = (systemRows || []).map((a) => mapActivity(a, 'system'));
            const nsRows = Array.isArray(nextStepsResponse) ? nextStepsResponse : (nextStepsResponse?.data || []);
            const nextStepActivities = nsRows.map((s) => ({
                id: s.id || `next-step-${dealId}-${s.action || ''}`,
                kind: 'activity',
                source: 'user',
                type: 'next_step',
                subject: s.action || 'Next step',
                body: '',
                dueDate: s.due_date || s.dueDate || '',
                createdAt: s.updated_at || s.created_at || s.updatedAt || s.createdAt || new Date().toISOString(),
                createdBy: null,
            }));
            const merged = [...userActivities, ...nextStepActivities, ...systemActivities].sort((a, b) =>
                String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
            );
            setDrawerItems((prev) => (append ? [...prev, ...merged] : merged));
            setDrawerPage(page);
            setDrawerHasMore((userRows?.length || 0) >= DRAWER_PER_PAGE || (systemRows?.length || 0) >= DRAWER_PER_PAGE);
        } catch (err) {
            console.warn('[DealSidebarTimeline] drawer load failed', err);
            if (!append) setDrawerItems([]);
            setDrawerHasMore(false);
        } finally {
            setDrawerLoading(false);
        }
    }, [dealId]);

    useEffect(() => {
        loadTimeline();
    }, [loadTimeline]);

    useEffect(() => {
        if (initialFilter === 'comments' || initialFilter === 'comment') setFilter('comments');
        else if (initialFilter === 'activity') setFilter('activity');
        else if (initialFilter) setFilter(initialFilter);
    }, [initialFilter]);

    useEffect(() => {
        if (!dealId) return;
        if (autoOpenDrawer) {
            const t = setTimeout(() => {
                const card = document.getElementById('sidebarTimeline')?.closest('.sidebar-card');
                card?.classList.remove('collapsed');
                if (initialFilter === 'comments' || initialFilter === 'comment') {
                    setFilter('comments');
                    setDrawerFilter('comments');
                }
                setDrawerOpen(true);
                loadDrawerPage(1, { append: false });
            }, 650);
            return () => clearTimeout(t);
        }
    }, [dealId, autoOpenDrawer, initialFilter, loadDrawerPage]);

    useEffect(() => {
        if (!drawerOpen || !dealId) return;
        if (drawerItems.length === 0 && !drawerLoading) {
            loadDrawerPage(1, { append: false });
        }
    }, [drawerOpen, dealId, drawerItems.length, drawerLoading, loadDrawerPage]);

    const highlightHandledRef = useRef(null);
    useEffect(() => {
        if (!highlightId) return;
        if (highlightHandledRef.current === String(highlightId)) return;
        const t = setTimeout(() => {
            const scrollElIntoContainer = (el) => {
                const drawerBody = document.getElementById('activityDrawerBody');
                const sidebarTimeline = document.getElementById('sidebarTimeline');
                const container = el.closest('.activity-drawer-body') ? drawerBody
                    : el.closest('#sidebarTimeline') ? (sidebarTimeline?.parentElement || sidebarTimeline)
                    : drawerBody || sidebarTimeline;
                if (container && el) {
                    const cRect = container.getBoundingClientRect();
                    const eRect = el.getBoundingClientRect();
                    const offset = eRect.top - cRect.top + container.scrollTop - (cRect.height / 2) + (eRect.height / 2);
                    try { container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' }); } catch {}
                } else if (el) {
                    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            };
            const doHighlight = () => {
                const el = document.querySelector(`[data-activity-id="${highlightId}"]`);
                if (el) {
                    highlightHandledRef.current = String(highlightId);
                    const needsDrawer = !!el.closest('.activity-drawer-feed');
                    if (needsDrawer && !drawerOpen) setDrawerOpen(true);
                    if (el.closest('#sidebarTimeline') || needsDrawer) {
                        setFilter('all');
                        setDrawerFilter('all');
                    }
                    requestAnimationFrame(() => scrollElIntoContainer(el));
                    el.classList.add('ring-2', 'ring-primary', 'rounded');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'rounded'), 2500);
                }
            };
            doHighlight();
            setTimeout(doHighlight, 600);
        }, 700);
        return () => clearTimeout(t);
    }, [highlightId, items, drawerItems, dealId]);

    useEffect(() => {
        if (!highlightId) highlightHandledRef.current = null;
    }, [highlightId]);

    const openDrawer = () => {
        if (!dealId) {
            ToastManager.show({ title: 'Activity', message: 'Save the deal first to view activity history.', tone: 'warning' });
            return;
        }
        setDrawerOpen(true);
        setDrawerFilter('all');
        setDrawerDateRange('all');
        setDrawerSearch('');
        setDrawerItems([]);
        loadDrawerPage(1, { append: false });
    };

    const closeDrawer = () => setDrawerOpen(false);

    const applyMentionPick = (source, pick) => {
        const input = source === 'drawer' ? drawerInputRef.current : sidebarInputRef.current;
        const value = comment;
        const cursor = mentionCtx.cursor ?? value.length;
        const before = value.slice(0, cursor);
        const match = before.match(/@([a-zA-Z0-9._-]*)$/);
        if (!match) return;
        const start = cursor - match[0].length;
        const next = `${value.slice(0, start)}@${pick.token} ${value.slice(cursor)}`;
        setComment(next);
        setMentionCtx({ source: null, cursor: 0 });
        requestAnimationFrame(() => {
            if (!input) return;
            const nextPos = start + pick.token.length + 2;
            input.focus();
            input.setSelectionRange(nextPos, nextPos);
        });
    };

    const onCommentInput = (source, e) => {
        const el = e.target;
        setComment(el.value);
        const before = el.value.slice(0, el.selectionStart ?? el.value.length);
        const match = before.match(/@([a-zA-Z0-9._-]*)$/);
        setMentionCtx(match
            ? { source, cursor: el.selectionStart ?? el.value.length }
            : { source: null, cursor: 0 });
    };

    const submitComment = async () => {
        const body = comment.trim();
        if (!dealId) {
            ToastManager.show({ title: 'Comment', message: 'Save the deal first to add comments.', tone: 'warning' });
            return;
        }
        if (!body) return;
        const mentions = extractMentionIdsFromText(body, users);
        setSending(true);
        try {
            await ActivityService.addComment('deal', dealId, body, mentions, isInternal);
            setComment('');
            setMentionCtx({ source: null, cursor: 0 });
            await loadTimeline();
            if (drawerOpen) await loadDrawerPage(1, { append: false });
            window.dispatchEvent(new CustomEvent('dealActivitiesChanged', { detail: { dealId } }));
        } catch (err) {
            ToastManager.show({ title: 'Comment', message: err?.message || 'Failed to add comment.', tone: 'error' });
        } finally {
            setSending(false);
        }
    };

    const deleteItem = async (id) => {
        if (!id) return;
        const ok = await confirmAlert({
            title: 'Delete activity',
            message: 'Delete this activity?',
            confirmLabel: 'Delete',
            confirmClass: 'btn-danger',
        });
        if (!ok) return;
        try {
            await ActivityService.deleteActivity(id);
            await loadTimeline();
            if (drawerOpen) await loadDrawerPage(1, { append: false });
        } catch (err) {
            ToastManager.show({ title: 'Activity', message: err?.message || 'Delete failed.', tone: 'error' });
        }
    };

    const counts = useMemo(() => {
        const comments = items.filter(isCommentItem).length;
        return { all: items.length, comments, activity: items.length - comments };
    }, [items]);

    const visible = items.filter((item) => {
        if (filter === 'comments') return isCommentItem(item);
        if (filter === 'activity') return !isCommentItem(item);
        return true;
    }).slice(0, 5);

    const drawerCounts = useMemo(() => {
        const comments = drawerItems.filter(isCommentItem).length;
        return { all: drawerItems.length, comments, activity: drawerItems.length - comments };
    }, [drawerItems]);

    const drawerVisible = useMemo(() => {
        const search = drawerSearch.trim().toLowerCase();
        const filtered = drawerItems.filter((item) => {
            if (drawerFilter === 'comments' && !isCommentItem(item)) return false;
            if (drawerFilter === 'activity' && isCommentItem(item)) return false;
            if (drawerDateRange !== 'all') {
                const days = Number(drawerDateRange || 0);
                if (days > 0) {
                    const createdTs = new Date(item.createdAt || 0).getTime();
                    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
                    if (!createdTs || createdTs < cutoff) return false;
                }
            }
            if (search) {
                const haystack = [item.subject, item.body, item.description, item.systemEventType, item.type]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                if (!haystack.includes(search)) return false;
            }
            return true;
        });

        const getItemTime = (item) => new Date(item.createdAt || 0).getTime();
        if (drawerSortOrder === 'alpha_asc' || drawerSortOrder === 'alpha_desc') {
            const getSortText = (item) => (item.subject || item.body || item.description || '').toLowerCase();
            filtered.sort((a, b) => {
                const cmp = getSortText(a).localeCompare(getSortText(b));
                return drawerSortOrder === 'alpha_asc' ? cmp : -cmp;
            });
        } else {
            filtered.sort((a, b) => getItemTime(b) - getItemTime(a));
        }
        return filtered;
    }, [drawerItems, drawerFilter, drawerDateRange, drawerSearch, drawerSortOrder]);

    const drawerGroups = useMemo(() => {
        const groups = new Map();
        drawerVisible.forEach((item) => {
            const key = activityDrawerDateLabel(item.createdAt);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item);
        });
        return [...groups.entries()];
    }, [drawerVisible]);

    const currentUserId = auth.getSession()?.userId || null;

    const renderItem = (act, { allowDelete = true, asListItem = false } = {}) => {
        const name = userMap[String(act.userId || act.createdBy)] || (act.kind === 'system' ? 'System' : 'Unknown');
        const time = formatTs(act.createdAt);
        const type = String(act.type || '').toLowerCase();
        const isSystem = act.kind === 'system' || act.source === 'system';
        let detail = act.body || act.description || act.subject || '—';
        if (type === 'task' && act.subject) detail = `Task: ${act.subject}`;
        else if (type === 'next_step') detail = act.subject || 'Next step';
        else if (type === 'email' && act.subject) detail = `Email: ${act.subject}`;
        const title = isSystem
            ? (act.subject || act.systemEventType || 'System')
            : (type === 'comment' || type === 'note' ? 'Comment' : (act.subject || type || 'Activity'));
        const isOwner = currentUserId && String(act.userId || act.createdBy) === String(currentUserId);
        const canDelete = allowDelete && isOwner && !isSystem && !act.deletedAt && type !== 'next_step';

        const content = (
            <div className={`sidebar-timeline-item is-compact${isSystem ? ' is-system' : ''}`} data-activity-id={act.id}>
                <div className="crm-activity-line1">
                    <span className={`crm-activity-type-dot ${isCommentItem(act) ? 'is-comment' : 'is-edit'}`} aria-hidden="true" />
                    <strong className="crm-activity-subject">{title}</strong>
                    <time className="crm-activity-time">{time}</time>
                    {(act.isInternal) ? <Badge variant="secondary" className="internal-badge">Internal</Badge> : null}
                </div>
                <div className="crm-activity-line2">
                    <MentionText text={detail} />
                    {' · '}
                    {name}
                </div>
                {canDelete && (
                    <div className="crm-activity-actions">
                        <button type="button" className="link-btn sidebar-delete-btn" onClick={() => deleteItem(act.id)}>Delete</button>
                    </div>
                )}
            </div>
        );
        return asListItem ? <li key={act.id}>{content}</li> : <React.Fragment key={act.id}>{content}</React.Fragment>;
    };

    const toggleDrawerSort = (sort) => {
        setDrawerSortOrder((prev) => (prev === sort ? '' : sort));
    };

    return (
        <>
            <div className="sidebar-card">
                <div className="sidebar-card-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    Comments & Timeline
                    <button
                        type="button"
                        className="sidebar-card-expand"
                        id="sidebarTimelineExpandBtn"
                        aria-label="Expand timeline"
                        title="Expand timeline"
                        onClick={openDrawer}
                    >
                        ⤢
                    </button>
                </div>
                <div className="timeline-filter-pills" id="sidebarTimelineFilters">
                    <button type="button" className={`timeline-filter-pill${filter === 'all' ? ' is-active' : ''}`} onClick={() => setFilter('all')}>
                        All {counts.all}
                    </button>
                    <button type="button" className={`timeline-filter-pill${filter === 'comments' ? ' is-active' : ''}`} onClick={() => setFilter('comments')}>
                        Comments {counts.comments}
                    </button>
                    <button type="button" className={`timeline-filter-pill${filter === 'activity' ? ' is-active' : ''}`} onClick={() => setFilter('activity')}>
                        Activity {counts.activity}
                    </button>
                </div>
                <div className="sidebar-timeline" id="sidebarTimeline">
                    {!visible.length ? (
                        <div className="sidebar-timeline-item is-compact">
                            <div className="crm-activity-line1">
                                <span className="crm-activity-type-dot is-edit" aria-hidden="true" />
                                <strong className="crm-activity-subject">No activity yet</strong>
                                <time className="crm-activity-time">—</time>
                            </div>
                            <div className="crm-activity-line2">Activity will appear here as the deal progresses</div>
                        </div>
                    ) : visible.map((act) => renderItem(act))}
                </div>
                <button type="button" className="premium-add-btn" id="sidebarViewAllActivityBtn" onClick={openDrawer}>
                    View all activity
                </button>
                <div className="sidebar-comment-input">
                    <Input unstyled
                        ref={sidebarInputRef}
                        type="text"
                        className="form-input"
                        id="sidebarCommentInput"
                        placeholder={dealId ? 'Enter comment' : 'Save deal to comment'}
                        maxLength={1000}
                        autoComplete="off"
                        disabled={!dealId || sending}
                        value={comment}
                        onChange={(e) => onCommentInput('sidebar', e)}
                        onFocus={(e) => onCommentInput('sidebar', e)}
                        onBlur={() => setTimeout(() => setMentionCtx((c) => (c.source === 'sidebar' ? { source: null, cursor: 0 } : c)), 120)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitComment();
                            }
                        }}
                    />
                    <button
                        type="button"
                        className="sidebar-send-btn"
                        id="sidebarCommentSendBtn"
                        aria-label="Send comment"
                        disabled={!dealId || sending || !comment.trim()}
                        onClick={submitComment}
                    >
                        ➤
                    </button>
                </div>
                <MentionMenu
                    users={users}
                    value={comment}
                    cursor={mentionCtx.cursor}
                    visible={mentionCtx.source === 'sidebar'}
                    onPick={(u) => applyMentionPick('sidebar', u)}
                />
                <div className="sidebar-comment-meta">
                    <span id="sidebarCommentCharCount">{comment.length}/1000</span>
                </div>
                <label className="sidebar-comment-internal" title="Only visible to team members with deal access">
                    <NativeCheckbox unstyled id="sidebarCommentInternal" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                    {' '}Internal note
                </label>
            </div>

            <Drawer
                open={drawerOpen}
                onOpenChange={(next) => { if (!next) closeDrawer(); else setDrawerOpen(true); }}
                direction="right"
                shouldScaleBackground={false}
            >
                <DrawerContent
                    unstyled
                    className="activity-drawer"
                    overlayClassName="activity-drawer-overlay active"
                    aria-labelledby="activityDrawerTitle"
                >
                        <header className="activity-drawer-header">
                            <div>
                                <p className="activity-drawer-eyebrow" id="activityDrawerDealName">{dealName || 'Deal'}</p>
                                <DrawerTitle unstyled id="activityDrawerTitle">Comments & Timeline</DrawerTitle>
                            </div>
                            <button type="button" className="activity-drawer-close" onClick={closeDrawer} aria-label="Close activity drawer">×</button>
                        </header>
                        <div className="activity-drawer-controls">
                            <Input unstyled
                                type="text"
                                id="activityDrawerSearch"
                                className="form-input"
                                placeholder="Search comments and activity"
                                autoComplete="off"
                                value={drawerSearch}
                                onChange={(e) => setDrawerSearch(e.target.value)}
                            />
                            <div className="timeline-filter-pills activity-drawer-pills" id="activityDrawerFilterRow">
                                <button type="button" className={`timeline-filter-pill${drawerFilter === 'all' ? ' is-active' : ''}`} onClick={() => setDrawerFilter('all')}>
                                    All {drawerCounts.all}
                                </button>
                                <button type="button" className={`timeline-filter-pill${drawerFilter === 'comments' ? ' is-active' : ''}`} onClick={() => setDrawerFilter('comments')}>
                                    Comments {drawerCounts.comments}
                                </button>
                                <button type="button" className={`timeline-filter-pill${drawerFilter === 'activity' ? ' is-active' : ''}`} onClick={() => setDrawerFilter('activity')}>
                                    Activity {drawerCounts.activity}
                                </button>
                                <Separator orientation="vertical" className="activity-drawer-pill-divider" />
                                <button type="button" className={`timeline-filter-pill${drawerDateRange === 'all' ? ' is-active' : ''}`} onClick={() => setDrawerDateRange('all')}>All time</button>
                                <button type="button" className={`timeline-filter-pill${drawerDateRange === '7' ? ' is-active' : ''}`} onClick={() => setDrawerDateRange('7')}>7 days</button>
                                <button type="button" className={`timeline-filter-pill${drawerDateRange === '30' ? ' is-active' : ''}`} onClick={() => setDrawerDateRange('30')}>30 days</button>
                                <button type="button" className={`timeline-filter-pill${drawerDateRange === '90' ? ' is-active' : ''}`} onClick={() => setDrawerDateRange('90')}>90 days</button>
                                <Separator orientation="vertical" className="activity-drawer-pill-divider" />
                                <button
                                    type="button"
                                    className={`timeline-filter-pill${drawerSortOrder === 'alpha_asc' ? ' is-active' : ''}`}
                                    title="Sort A-Z"
                                    onClick={() => toggleDrawerSort('alpha_asc')}
                                >
                                    A-Z
                                </button>
                                <button
                                    type="button"
                                    className={`timeline-filter-pill${drawerSortOrder === 'alpha_desc' ? ' is-active' : ''}`}
                                    title="Sort Z-A"
                                    onClick={() => toggleDrawerSort('alpha_desc')}
                                >
                                    Z-A
                                </button>
                            </div>
                        </div>
                        <div className="activity-drawer-body" id="activityDrawerBody">
                            <div id="activityDrawerFeed" className="activity-drawer-feed">
                                {drawerLoading && !drawerItems.length && <p className="field-hint">Loading…</p>}
                                {!drawerLoading && !drawerVisible.length && (
                                    <div className="activity-empty">No activity found for this filter.</div>
                                )}
                                {drawerGroups.map(([label, groupItems]) => (
                                    <section className="activity-drawer-group" key={label}>
                                        <h3 className="activity-drawer-group-title">{label}</h3>
                                        <ul className="activity-drawer-group-list">
                                            {groupItems.map((act) => renderItem(act, { asListItem: true }))}
                                        </ul>
                                    </section>
                                ))}
                                {drawerVisible.length > 0 && !drawerHasMore && (
                                    <div className="activity-drawer-end">End of activity</div>
                                )}
                            </div>
                            {drawerHasMore && (
                                <button
                                    type="button"
                                    className="activity-load-more"
                                    id="activityDrawerLoadMoreBtn"
                                    disabled={drawerLoading}
                                    onClick={() => loadDrawerPage(drawerPage + 1, { append: true })}
                                >
                                    Load more
                                </button>
                            )}
                        </div>
                        <footer className={`activity-drawer-footer${drawerFilter === 'activity' ? ' hidden' : ''}`}>
                            <div className="sidebar-comment-input">
                                <Input unstyled
                                    ref={drawerInputRef}
                                    type="text"
                                    className="form-input"
                                    id="activityDrawerCommentInput"
                                    placeholder={dealId ? 'Write a comment...' : 'Save deal to comment'}
                                    maxLength={1000}
                                    autoComplete="off"
                                    disabled={!dealId || sending}
                                    value={comment}
                                    onChange={(e) => onCommentInput('drawer', e)}
                                    onFocus={(e) => onCommentInput('drawer', e)}
                                    onBlur={() => setTimeout(() => setMentionCtx((c) => (c.source === 'drawer' ? { source: null, cursor: 0 } : c)), 120)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            submitComment();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    className="sidebar-send-btn"
                                    id="activityDrawerCommentSendBtn"
                                    aria-label="Send comment"
                                    disabled={!dealId || sending || !comment.trim()}
                                    onClick={submitComment}
                                >
                                    ➤
                                </button>
                            </div>
                            <MentionMenu
                                users={users}
                                value={comment}
                                cursor={mentionCtx.cursor}
                                visible={mentionCtx.source === 'drawer'}
                                onPick={(u) => applyMentionPick('drawer', u)}
                            />
                            <div className="activity-drawer-footer-meta">
                                <span id="activityDrawerCommentCharCount">{comment.length}/1000</span>
                                <span>Ctrl+Enter to send</span>
                            </div>
                        </footer>
                </DrawerContent>
            </Drawer>
        </>
    );
}
